import type {
  FrameworkSummaryStatementWire,
  FrameworkSummaryWire,
} from "../../../../../api/types"
import type { Verdict } from "../../studio/studioTypes"
import {
  deriveFrameworkVerdict,
  type FrameworkDerivation,
  type InquiryState,
  type UnitFacts,
} from "./deriveFrameworkVerdict"
import { comparePosition } from "./position"

/**
 * STEP C3-b2: THE REGULATIONS THAT ARE NOT OPEN, FROM THE SERVER'S SUMMARY.
 *
 * The paged review loads one regulation's cards. The navigator, the derivation glyphs,
 * the primary action, the labels and the card order of every OTHER regulation come from
 * `/open-items` `frameworks` (C3-s), whose fields are the server's port of the screen's
 * own derivations (`statementCardsByFramework`, `cardPosition`, `collisionNumbers`, R2),
 * pinned to this code by the shared flowParity fixture.
 */

/** A statement's place on screen without its card: what the labels, the card order,
 *  the mark targets and the "all excluded" sentence read. */
export interface CardRef {
  id: string
  groupIndex: number
  excluded: boolean
}

/** Regulations by the earliest place any of their statements cites, then by display
 *  name — `documentOrder` over the server's first positions instead of cards. */
export function documentOrderFromSummary(
  summary: readonly FrameworkSummaryWire[],
  nameOf: (framework: string) => string,
): string[] {
  // A part the server could not read as a number arrives as `null` (JSON has no NaN).
  // The screen's own `cardPosition` holds NaN there, and NaN and 0 compare differently
  // (`null - 3` is -3, `NaN - 3` is NaN), so it is turned back into NaN before the
  // shared comparator runs: the regulation order then matches flag off exactly.
  const first = new Map(
    summary.map((f) => [
      f.framework,
      f.first_position
        ? (f.first_position.map((part) =>
            part === null ? Number.NaN : part,
          ) as [number, number, number])
        : null,
    ]),
  )
  return summary
    .map((f) => f.framework)
    .sort(
      (a, b) =>
        comparePosition(first.get(a) ?? null, first.get(b) ?? null) ||
        nameOf(a).localeCompare(nameOf(b)),
    )
}

/** The facts `deriveFrameworkVerdict` reads, from a summary statement. The article
 *  lists are one-item stand-ins: the rules read only their emptiness and, for R2, the
 *  size of their union — which the server sends whole (`non_compliant_article_count`,
 *  applied in `deriveFromSummary`), so no article-sized list is ever built for a
 *  regulation that is not on screen. */
export function unitFactsFromSummary(
  s: FrameworkSummaryStatementWire,
): UnitFacts {
  return {
    verdict: (s.verdict as Verdict | null) ?? null,
    scopeAll: s.scope_all,
    articleNumbers: s.has_articles ? [s.statement_id] : [],
    resolvedArticleNumbers:
      s.resolved_article_count > 0 ? [s.statement_id] : [],
    unresolvedRefs: s.unresolved_refs,
  }
}

/** A regulation's live units (excluded statements count for nothing, as on screen). */
export function unitsFromSummary(f: FrameworkSummaryWire): UnitFacts[] {
  return f.statements.filter((s) => !s.excluded).map(unitFactsFromSummary)
}

/** `deriveFrameworkVerdict` for a regulation that is not open: the same rules, with
 *  R2's non-compliant article count taken from the server (the union of the article
 *  lists, which the summary never ships). */
export function deriveFromSummary(
  f: FrameworkSummaryWire,
  inquiryState: InquiryState,
): FrameworkDerivation | null {
  const derivation = deriveFrameworkVerdict(unitsFromSummary(f), inquiryState)
  if (derivation?.rule === "R2")
    return {
      ...derivation,
      nonCompliantArticles: f.non_compliant_article_count,
    }
  return derivation
}

/** Card refs in card order (the summary lists statements in the screen's card order). */
export function cardRefsFromSummary(f: FrameworkSummaryWire): CardRef[] {
  return f.statements.map((s) => ({
    id: s.statement_id,
    groupIndex: s.group_index,
    excluded: s.excluded,
  }))
}

/** Title numbers across the run, by statement id (the server numbers run-wide, like
 *  `collisionNumbers`; one regulation alone would restart the count). */
export function collisionsFromSummary(
  summary: readonly FrameworkSummaryWire[],
): Map<string, number | null> {
  const out = new Map<string, number | null>()
  for (const f of summary)
    for (const s of f.statements) out.set(s.statement_id, s.collision_number)
  return out
}
